import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { fonts } from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import { WYSIWYGStyles } from '@stories/Components/Content/WYSIWYG/WYSIWYG.styles';
import Paragraph from '@stories/Components/Global/Typography/Paragraph';
import Heading from '@stories/Components/Global/Typography/Heading';
import { m } from 'framer-motion';
import { transparentize } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled.div `
  padding: 40px 0px;

  @media ${until(Device.TabletLarge)} {
    background-color: ${brand.grey.grey96};
  }
`;
const Tabs = styled.div.attrs({ role: 'tabset' }) `
  gap: 4px;
  grid-column: 1 / -1;

  @media ${until(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    z-index: 2;
    background-color: ${brand.grey.grey96};
  }

  @media ${from(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
    grid-column: span 3;
    min-height: 500px;
  }

  height: max-content;
  max-height: 100%;
  position: sticky;
  top: 15px;
`;
const Tab = styled.button.attrs((props) => ({
    whileHover: 'visible',
    initial: 'hidden',
    animate: props['data-current'] ? 'visible' : 'hidden',
    exit: 'hidden',
    type: 'button',
    role: 'tab',
})) `
  padding: ${fluid(15, 22)} ${fluid(15, 27)};
  color: ${brand.black};
  background-color: ${transparentize(0.75, '#6F9D20')};
  transition: all 0.25s ease-in-out;
  font-size: ${fluid(20, 24)};
  cursor: pointer;
  position: relative;
  align-items: center;
  border: 0;
  ${fonts.DaxPro.Regular};

  @media ${until(Device.TabletLarge)} {
    text-align: center;
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr 28px;
    min-height: 72px;
  }

  ${(props) => props['data-wide'] &&
    css `
      grid-column: span 2;
    `}

  --color: ${brand.white};
  &:hover {
    --color: ${brand.black};
  }

  &[data-current='true'] {
    --color: ${brand.white};
    color: ${brand.white};
    ${fonts.DaxPro.Medium};
  }
`;
const BackgroundAnimation = styled(m.div) `
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: #6f9d20;
  z-index: -1;
`;
const Arrow = styled(m.div).attrs((props) => ({
    whileHover: 'visible',
    initial: 'hidden',
    animate: props['data-hovered'] || props['data-current'] ? 'visible' : 'hidden',
    variants: {
        hidden: {
            x: -25,
            opacity: 0,
        },
        visible: {
            x: 0,
            opacity: 1,
        },
    },
    transition: {
        duration: 0.35,
    },
})) `
  width: ${fluid(15, 28)};
  height: ${fluid(15, 28)};
  color: var(--color);
  transition: color 0.25s ease-in-out;

  svg {
    width: 100%;
    height: 100%;
  }
`;
const TabTitle = styled(Heading) `
  ${fonts.DaxPro.Bold};
  line-height: ${fluid(24, 36)};
`;
const TabButtonTitle = styled(Heading) `
  color: ${brand.black};
  transition: all 0.25s ease-in-out;
  font-size: ${fluid(20, 24)};
  cursor: pointer;
  line-height: normal;
  margin-bottom: 0;
  ${fonts.DaxPro.Regular};

  --color: ${brand.white};
  &:hover {
    --color: ${brand.black};
  }

  &[data-current='true'] {
    --color: ${brand.white};
    color: ${brand.white};
    ${fonts.DaxPro.Medium};
  }

`;
const Content = styled.div `
  padding-top: 25px;
  grid-column: 1 / -1;

  p,
  li {
    font-size: ${fluid(16, 20)} !important;
  }

  p {
    line-height: ${fluid(24, 36)} !important;
  }

  ${TabTitle} {
    font-size: 24px !important;
  }

  @media ${until(Device.TabletLarge)} {
    overflow: hidden;
  }

  @media ${from(Device.TabletLarge)} {
    padding-top: 18px;
    grid-column: 4 / -1;
  }
`;
const Layout = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-current'] ? 'visible' : 'hidden',
    exit: 'hidden-reverse',
    variants: {
        hidden: {
            x: -25,
            opacity: 0,
            height: 0,
        },
        visible: {
            x: 0,
            opacity: 1,
            height: '100%',
        },
        'hidden-reverse': {
            x: 25,
            opacity: 0,
            height: 0,
        },
    },
    transition: {
        duration: 0.35,
    },
})) `
  gap: 25px;
  height: 100%;

  @media ${until(Device.TabletLarge)} {
    display: ${(props) => (props['data-current'] ? 'flex' : 'none')};
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: ${(props) => (props.type === 'full' ? '1fr' : `1fr ${fluid(150, 528)}`)};
    ${(props) => !props['data-current'] && 'display: none;'}
  }
`;
const MainContent = styled.div `
  overflow: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;

  div[id^='react_']:not(:has(+ div[id^='react_'])) & {
    overflow: unset;
  }
`;
const ImageContainer = styled.div `
  @media ${until(Device.TabletLarge)} {
    img {
      width: 100%;
    }
  }
`;
const WYSIWYGOverrides = css `
  img {
    max-width: ${fluid(150, 200)};
  }

  a {
    text-decoration: underline;
  }

  ul {
    padding-left: ${fluid(15, 20)};

    li {
      padding-left: ${fluid(15, 20)};
      position: relative;
      margin-bottom: ${fluid(8, 12)};
      line-height: 1.2em;

      &::before {
        content: '';
        position: absolute;
        width: 10px;
        height: 10px;
        background-color: rgb(133, 154, 62);
        top: 0.5em;
        left: 0;
        display: block;
        border-radius: 50%;
      }
    }
  }
`;
const ContentWrapper = styled.div `
  position: absolute;
  inset: 0;

  div[id^='react_']:not(:has(+ div[id^='react_'])) & {
    position: static;
  }

  @media ${until(Device.TabletLarge)} {
    position: relative;
  }
`;
const RichTextContent = styled(WYSIWYGStyles.Wrapper) `
  ${WYSIWYGOverrides}
  overflow: visible;
`;
const Files = styled.div `
  gap: ${fluid(15, 20)};
  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }
  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  ${() => ButtonStyles.Inner} {
    background-color: ${brand.grey.grey89} !important;
    color: ${brand.grey.grey20} !important;
  }

  ${() => ButtonStyles.HoverEffect} {
    color: ${brand.grey.grey89} !important;
  }
`;
const FAQList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const FAQWrapper = styled.div `
  display: flex;
  flex-direction: column;
  border-bottom: 2px solid ${brand.primary.alt};

  svg {
    width: ${fluid(10, 15)};
    margin-left: auto;

    path {
      stroke-width: 1px !important;
    }

    @media ${until(Device.TabletLarge)} {
      margin-right: 3px;
    }
    transition: transform 0.15s ease-in-out;
    transform: rotate(-90deg);
  }

  &[data-open='true'] {
    svg {
      transform: rotate(-270deg);
    }
  }
`;
const FAQTitle = styled(Paragraph) `
  cursor: pointer;
  user-select: none;
  display: grid;
  grid-template-columns: 1fr 40px;
  gap: 10px;
  align-items: center;

  ${fonts.DaxPro.Medium};

  @media ${until(Device.TabletLarge)} {
    font-size: 16px;
    line-height: 19px;
  }
`;
const FAQBody = styled(m.div).attrs(({ open }) => ({
    initial: 'hidden',
    animate: open ? 'visible' : 'hidden',
    variants: {
        hidden: {
            height: '0px',
        },
        visible: {
            height: 'auto',
        },
    },
})) `
  overflow: hidden;
`;
const FAQInner = styled(WYSIWYGStyles.Wrapper) `
  padding: 10px 0px;
  ${WYSIWYGOverrides}
`;
export const TabbedSectionStyles = {
    Tabs,
    Tab,
    BackgroundAnimation,
    Arrow,
    Content,
    Layout,
    MainContent,
    TabTitle,
    ImageContainer,
    ContentWrapper,
    RichTextContent,
    Files,
    FAQList,
    FAQWrapper,
    FAQTitle,
    FAQBody,
    FAQInner,
    Container,
    TabButtonTitle,
};
